import axios, { AxiosResponse } from "axios";
import { WebAPI, IFailCallbackParam } from "../constants";
import { getErrorStatus } from "./getErrorStatus";
import AuthService from "../../common/AuthService";

interface ICallbackParams extends IFailCallbackParam {
  /**
   * WebAPIでシミュレータのシミュレーションが成功した場合に呼び出されます
   * @callback success
   * @param {number} status - httpステータス
   */
  success: (status: number) => void;
}

export interface ISimulateModelRequestModel {
  /**
   * シミュレーションを行うモデルID
   */
  modelid: string;
  /**
   * プロセス値につけるタグ名
   */
  pvtag: string;
  /**
   * PID切り替えタイミングの値
   */
  switchpv?: number;
  /**
   * PID切り替えタイミングのステップ数
   */
  switchstep?: number;
  /**
   * 目標値
   */
  sp?: number;
  /**
   * 比例帯[%]
   */
  pb?: number;
  /**
   * 積分時間[sec]
   */
  ti?: number;
  /**
   * 微分時間[sec]
   */
  td?: number;
  /**
   * 制御レンジ上限
   */
  rangemax?: number;
  /**
   * 制御レンジ下限
   */
  rangemin?: number;
  /**
   * シミュレーションのステップ数
   */
  simstep?: number;
}

function AxiosPost(
  url: string,
  params: ISimulateModelRequestModel,
  token?: string
) {
  if (token) {
    return axios.post(url, JSON.stringify(params), {
      headers: {
        "Content-Type": "application/json",
        Authorization: "Bearer " + token,
      },
    });
  } else {
    return axios.post(url, JSON.stringify(params), {
      headers: { "Content-Type": "application/json" },
    });
  }
}

interface IAxiosInternalParams extends ICallbackParams {
  promise: Promise<AxiosResponse>;
}

function AxiosInternal(params: IAxiosInternalParams) {
  params.promise
    .then((res) => {
      params.success(res.status);
    })
    .catch((err) => {
      const { id, status } = getErrorStatus(err);
      params.fail(id, status);
    });
}

export interface ISimulateModelParams
  extends ISimulateModelRequestModel,
    ICallbackParams {
  /**
   * 認証オブジェクト
   */
  authService?: AuthService;
  /**
   * プロジェクトID
   */
  projectid: string;
}

/**
 * 学習モデルのPID切り替えシミュレーションを実行します
 * @function simulateModel
 * @param {ISimulateModelParams} params - 学習モデルのPID切り替えシミュレーションパラメータ
 */
 export function simulateModel(params: ISimulateModelParams): void {
  const { 
    authService, 
    projectid, 
    modelid,
    pvtag,
    switchpv,
    switchstep,
    sp,
    pb,
    ti,
    td,
    rangemax,
    rangemin,
    simstep
  } = params;
  const req: ISimulateModelRequestModel = {
    modelid: modelid,
    pvtag: pvtag,
    switchpv: switchpv,
    switchstep: switchstep,
    sp: sp,
    pb: pb,
    ti: ti,
    td: td,
    rangemax: rangemax,
    rangemin: rangemin,
    simstep: simstep,
  };
  const url = WebAPI.SIMULATION_MODEL(projectid);
  if (authService) {
    authService.getToken({
      success: (token) => {
        AxiosInternal({
          promise: AxiosPost(url, req, token),
          success: params.success,
          fail: (id, status) => {
            if (status === 401) {
              simulateModelRetry401(authService, url, req, params);
            } else {
              params.fail(id, status);
            }
          },
        });
      },
      fail: (err) => {
        const { id, status } = getErrorStatus(err);
        params.fail(id, status);
      },
    });
  } else {
    AxiosInternal({
      promise: AxiosPost(url, req),
      success: params.success,
      fail: params.fail,
    });
  }
}

/**
 * 学習モデルのPID切り替えシミュレーションを実行します
 * @function simulateModelRetry401
 * @param {AuthService} authService - 認証オブジェクト
 * @param {string} url - リクエストURL
 * @param {ISimulateModelRequestModel} req - リクエストパラメータ
 * @param {ICallbackParams} params - コールバックパラメータ
 */
function simulateModelRetry401(
  authService: AuthService,
  url: string,
  req: ISimulateModelRequestModel,
  params: ICallbackParams
): void {
  authService.updateToken({
    success: (token) => {
      AxiosInternal({
        promise: AxiosPost(url, req, token),
        success: params.success,
        fail: params.fail,
      });
    },
    fail: (err) => {
      const { id, status } = getErrorStatus(err);
      params.fail(id, status);
    },
  });
}